import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { type Component, createMemo, For, Show } from "solid-js";
import { Link, useLocation } from "@tanstack/solid-router";
import { useNavigate } from "@/lib/u-router";
import { cn, isAdminCapable } from "@bso/shared";
import {
  BarChart3,
  Bell,
  Bookmark,
  BookOpen,
  Bug,
  ChevronsUpDown,
  FileText,
  Flag,
  Flame,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Mail,
  Palette,
  Plug,
  Scan,
  Search,
  Shield,
  ShieldAlert,
  Star,
  Type,
  User,
  UserCircle,
  Users,
} from "lucide-solid";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SyncStatusPill } from "@/components/sync-status-pill";
import { Logo } from "@/components/ui/logo";
import { SidebarNavItem } from "@/components/ui/sidebar-nav-item";
import { safeRedirectTarget, useSession } from "@/lib/auth";
import { readActiveAccountSync } from "@/lib/accounts";
import { useI18n } from "@/providers/i18n-provider";
import { useSignOut } from "@/providers/boot-manager";
import { usePendingInvitationsCount } from "@/features/organizations/hooks/use-pending-invitations-count";
import { getLastReadBible } from "@/utils/last-read";

const inactiveClass =
  "text-muted-foreground/80 hover:bg-accent/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:bg-accent/5 transition-[background-color,color,box-shadow]";

interface AppSidebarProps {
  stats: () => unknown;
  onCloseSidebar?: () => void;
}

export function AppSidebar(props: AppSidebarProps) {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { promptSignOut } = useSignOut();
  const pendingInvitations = usePendingInvitationsCount();

  // URL-addressable switcher (#18): `?account=switch` opens it. The overlay
  // itself is mounted ONCE at the layout level (AccountSwitcherHost); this
  // trigger only flips the URL flag.
  const setSwitcherOpen = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        if (open) {
          next.account = "switch";
        } else {
          delete next.account;
        }
        return next;
      },
      replace: true,
    });
  };

  // Reactive view of the active account for the switcher trigger label. Reading
  // `session.data` registers the dependency so this recomputes when the session
  // changes (switch/sign-in re-primes it).
  const activeAccount = createMemo(() => {
    void session.data;
    return readActiveAccountSync();
  });

  const activeLabel = () => {
    const a = activeAccount();
    if (a?.kind === "real") {
      return a.displayName;
    }
    return t(msg`Guest`);
  };

  const activeInitial = () => {
    const a = activeAccount();
    return (a?.displayName ?? a?.email ?? "?").charAt(0);
  };

  const ADMIN_ITEMS: {
    to: string;
    label: MessageDescriptor;
    icon: Component<{ class?: string }>;
  }[] = [
    { to: "/admin", label: msg`Overview`, icon: LayoutDashboard },
    { to: "/admin/users", label: msg`Users`, icon: Users },
    { to: "/admin/metrics", label: msg`Metrics`, icon: BarChart3 },
    { to: "/admin/flags", label: msg`Feature Flags`, icon: Flag },
    { to: "/admin/integrations", label: msg`Integrations`, icon: Plug },
    { to: "/organizations", label: msg`Organizations`, icon: Users },
    { to: "/invitations", label: msg`Invitations`, icon: Mail },
    { to: "/admin/storage", label: msg`Storage`, icon: Shield },
    { to: "/admin/audit", label: msg`Audit Log`, icon: Scan },
    { to: "/admin/themes", label: msg`Themes`, icon: Palette },
    { to: "/admin/theme-audit", label: msg`Theme Audit`, icon: ShieldAlert },
    { to: "/admin/fonts", label: msg`Fonts`, icon: Type },
    { to: "/admin/bible-versions", label: msg`Bible Versions`, icon: BookOpen },
  ];

  const handleSignOut = () => {
    props.onCloseSidebar?.();
    promptSignOut();
  };

  return (
    <div class="flex flex-col h-full pt-[var(--safe-area-inset-top)] pb-[var(--safe-area-inset-bottom)] pl-[var(--safe-area-inset-left)]">
      <div class="flex h-16 items-center px-6 shrink-0">
        <Logo size="sm" />
      </div>

      <Separator />

      <nav class="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2 scrollbar-hide">
        {/* Core Bible Navigation */}
        <div class="px-3 mb-1 mt-2">
          <h3 class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            {t(msg`Biblical Navigation`)}
          </h3>
        </div>
        <SidebarNavItem
          to="/bible/$version/$bookAbbr/$chapter"
          params={{
            version: getLastReadBible().version,
            bookAbbr: getLastReadBible().bookAbbr,
            chapter: getLastReadBible().chapter,
          }}
          label={t(msg`Bible Reader`)}
          icon={BookOpen}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/search"
          label={t(msg`Search`)}
          icon={Search}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/dictionary"
          label={t(msg`Strong's Dictionary`)}
          icon={BookOpen}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/highlights"
          label={t(msg`Highlights`)}
          icon={Star}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/notes"
          label={t(msg`Personal Notes`)}
          icon={FileText}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/bookmarks"
          label={t(msg`Bookmarks`)}
          icon={Bookmark}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/plans"
          label={t(msg`Reading Plans`)}
          icon={ListChecks}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/analytics"
          label={t(msg`Progress`)}
          icon={BarChart3}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/achievements"
          label={t(msg`Achievements`)}
          icon={Flame}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/notifications"
          label={t(msg`Notifications`)}
          icon={Bell}
          count={pendingInvitations()}
          onClick={() => props.onCloseSidebar?.()}
        />

        <Show when={isAdminCapable(session.data?.user.role)}>
          <div class="mt-4 px-3 mb-1">
            <h3 class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {t(msg`Administration`)}
            </h3>
          </div>
          <For each={ADMIN_ITEMS}>
            {(item: {
              to: string;
              label: MessageDescriptor;
              icon: Component<{ class?: string }>;
            }) => (
              <SidebarNavItem
                to={item.to}
                label={t(item.label)}
                icon={item.icon}
                onClick={() => props.onCloseSidebar?.()}
              />
            )}
          </For>
        </Show>
      </nav>

      <Separator />

      {/* Bottom actions */}
      <div class="flex flex-col gap-0.5 p-2 shrink-0">
        {/* Global sync status (legacy parity: floating sync indicator) */}
        <div class="px-3 py-1.5">
          <SyncStatusPill class="!items-start" />
        </div>
        <SidebarNavItem
          to="/profile"
          label={t(msg`Profile`)}
          icon={User}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/debug"
          label={t(msg`Debug`)}
          icon={Bug}
          onClick={() => props.onCloseSidebar?.()}
        />

        {/* ── Account switcher (multi-account #18) ────────────────────────── */}
        <Separator class="my-1" />
        <button
          type="button"
          onClick={() => {
            setSwitcherOpen(true);
          }}
          class={cn(
            "flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors",
            inactiveClass,
          )}
          aria-label={t(msg`Switch account`)}
          data-testid="account-switcher-trigger"
        >
          <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-bold">
            <Show when={activeAccount()?.kind === "real"} fallback={<UserCircle class="h-4 w-4" />}>
              <span class="uppercase">{activeInitial()}</span>
            </Show>
          </span>
          <span class="min-w-0 flex-1 text-left">
            <span class="block truncate font-medium text-foreground">{activeLabel()}</span>
            <Show when={activeAccount()?.email}>
              <span class="block truncate text-xs text-muted-foreground/80">
                {activeAccount()?.email}
              </span>
            </Show>
          </span>
          <ChevronsUpDown class="h-4 w-4 shrink-0 opacity-60" />
        </button>

        <Show
          when={session.isAuthenticated()}
          fallback={
            <Link
              to="/login"
              search={{ redirect: safeRedirectTarget(location().pathname) }}
              onClick={() => props.onCloseSidebar?.()}
              class={cn(
                "mt-1 flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                "bg-primary/10 text-primary hover:bg-primary/20",
              )}
            >
              <User class="h-4 w-4" />
              {t(msg`Sign In`)}
            </Link>
          }
        >
          <Button
            variant="ghost"
            class={`mt-1 w-full justify-start gap-2.5 px-3 py-2.5 ${inactiveClass} hover:text-destructive`}
            onClick={handleSignOut}
          >
            <LogOut class="h-4 w-4" />
            {t(msg`Sign Out`)}
          </Button>
        </Show>
      </div>
    </div>
  );
}
